import { BranchFilter } from "@/data/models/BranchFilter";
import ClientPanel from "@/data/models/ClientPanel";
import { ClientFilter } from "@/data/models/ClientFilter";
import {
  DeviceFilter,
  matchesIresDeviceFilterWithContext,
  resolveIresDeviceFilterContext,
} from "@/data/models/DeviceFilter";
import {
  compareBySortOption,
  DeviceSortData,
  DeviceSortOrder,
} from "@/data/models/DeviceSortOrder";
import { IresDevice } from "@/data/models/IresDevice";
import User from "@/data/models/User";
import { isDeviceDeleted } from "@/utils/DeviceManagementUtils";

/**
 * The part of the hyDry device list's row model that filtering and sorting needs.
 *
 * The list's own `IresDeviceItemData` carries display state on top of this
 * (`lastDisplayedDate`, `matchingClientPanel`); the pipeline deliberately does not
 * read or write those, so it stays free of Vue reactivity.
 */
export interface SortableIresDeviceItem {
  serialNumber: string;
  iresDevice: IresDevice;
}

export interface IresDeviceFilterSortOptions {
  sortOrder: DeviceSortOrder;
  filter: DeviceFilter;
  clientFilter: ClientFilter;
  branchFilter: BranchFilter;
  /** Free-text query on serial and inventory number. Empty or unset disables it. */
  searchQuery?: string | null;
  targetUser: User | null;
  /**
   * The test-ComBox view lists devices regardless of who is responsible for the
   * project, so it is passed to the device filter as `ignoreResponsibleUsers`.
   */
  showTestComBoxes: boolean;
  /**
   * Restricts the list to devices of the target user's own projects. Unset counts
   * as not restricted, which is how the list has always passed it on.
   */
  restrictToMyDevices: boolean | undefined;
  /** When the client selector is hidden, the client filter must not apply. */
  isAllowedToViewClientSelector: boolean;
  /** Client panels by serial number, used to resolve inventory numbers and deletion. */
  clientPanels: Map<string, ClientPanel>;
  /**
   * Sort keys by serial number. Treated as opaque input: entries are seeded when a
   * device first arrives and refreshed by the rows as their program chain loads, so
   * recomputing them here would reorder the list while it is still loading.
   */
  deviceSortData: Map<string, DeviceSortData>;
}

/**
 * Lower-cases the query the way the list has always done it: via upper case first,
 * which is not the same as a single `toLowerCase()` for characters such as "ß"
 * (`"ß".toUpperCase()` is `"SS"`). Kept so inventory-number searches keep matching
 * exactly what they matched before.
 */
function normalizeSearchQuery(searchQuery?: string | null) {
  return searchQuery ? searchQuery.toUpperCase().toLowerCase() : "";
}

/** Devices are searchable by serial number and by inventory number. */
function matchesSearchQuery(
  item: SortableIresDeviceItem,
  normalizedQuery: string
) {
  return (
    item.iresDevice.serialNo.toLowerCase().includes(normalizedQuery) ||
    !!item.iresDevice.currentAssignment?.inventoryNumber
      ?.toLowerCase()
      .includes(normalizedQuery)
  );
}

/**
 * Filters and sorts the hyDry device list.
 *
 * Reads no reactive state and no session storage: the identity and capability state
 * is resolved once up front, and every per-device key is derived in a single decorate
 * pass instead of inside the comparator, which runs O(n log n) times. For a master
 * client with ~1000 devices that is the difference between four `JSON.parse` of the
 * whole client document per comparison and none at all.
 *
 * Filtering happens before sorting, and the sort is stable, so the result is the same
 * as filtering afterwards would give.
 *
 * Two behaviours are deliberately preserved and are surprising enough to look like
 * bugs:
 *
 * - A device is matched against the client filter only while the client selector is
 *   visible.
 * - The search filter **replaces** the deleted-device filter rather than adding to
 *   it, so a search surfaces devices whose client panel is deleted while the
 *   unfiltered list hides them.
 */
export function filterAndSortIresDevices<T extends SortableIresDeviceItem>(
  items: T[],
  options: IresDeviceFilterSortOptions
): T[] {
  const {
    sortOrder,
    filter,
    clientFilter,
    branchFilter,
    targetUser,
    showTestComBoxes,
    restrictToMyDevices,
    isAllowedToViewClientSelector,
    clientPanels,
    deviceSortData,
  } = options;

  const filterContext = resolveIresDeviceFilterContext(targetUser);
  const forcedDoesSeeAllDevices = restrictToMyDevices ? false : undefined;
  const searchQuery = normalizeSearchQuery(options.searchQuery);
  const applyInUsageSplit = filter === DeviceFilter.ALL_DEVICES;

  const matchesFilters = (item: T) => {
    if (
      isAllowedToViewClientSelector &&
      !clientFilter.doesMatchIresDevice(item.iresDevice)
    ) {
      return false;
    }

    if (!branchFilter.doesMatchIresDevice(item.iresDevice)) {
      return false;
    }

    if (
      !matchesIresDeviceFilterWithContext(
        filter,
        item.iresDevice,
        targetUser,
        filterContext,
        showTestComBoxes,
        forcedDoesSeeAllDevices
      )
    ) {
      return false;
    }

    if (searchQuery) {
      return matchesSearchQuery(item, searchQuery);
    }

    const clientPanel = clientPanels.get(item.serialNumber);

    return !clientPanel || !isDeviceDeleted(clientPanel);
  };

  const decorated = items.filter(matchesFilters).map((item) => ({
    item,
    sortData: deviceSortData.get(item.serialNumber),
    // Only the ALL_DEVICES view sorts by usage, so the check is skipped entirely
    // for every other filter rather than evaluated and thrown away.
    isInUsage:
      applyInUsageSplit &&
      matchesIresDeviceFilterWithContext(
        DeviceFilter.IN_USAGE,
        item.iresDevice,
        targetUser,
        filterContext
      ),
  }));

  decorated.sort((first, second) =>
    compareBySortOption(sortOrder, applyInUsageSplit, first, second)
  );

  return decorated.map((entry) => entry.item);
}
